import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrivacyPage = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const ownerName = settings?.owner_name || 'Muller Stéphane';
  const companyName = settings?.company_legal_name || 'Muller Construction SRL';
  const companyVat = settings?.company_vat || 'BE 0803.324.504';
  const companyAddress = settings?.company_address || 'Belgique';
  const companyCountry = settings?.company_country || 'Belgique';
  const companyPostalCode = settings?.company_postal_code || '';
  const dpoEmail = settings?.dpo_email || 'dpo@buildconnect.be';
  const privacyEmail = settings?.privacy_email || 'privacy@buildconnect.be';
  const platformName = settings?.platform_name || 'BuildConnect';
  const customContent = settings?.privacy_content;

  const fullAddress = [companyAddress, companyPostalCode, companyCountry].filter(Boolean).join(', ');

  if (customContent) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="privacy-page">
        <SEOHead
          title="Politique de Confidentialité"
          description="Politique de confidentialité et protection des données de BuildConnect. Conformité RGPD pour le secteur du bâtiment."
          path="/privacy"
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"><Button variant="ghost" className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button></Link>
          <h1 className="font-heading font-bold text-3xl text-[#111827] mb-8">Politique de Confidentialité</h1>
          <div className="prose prose-lg max-w-none text-[#374151] whitespace-pre-wrap" data-testid="privacy-custom-content">
            {customContent}
          </div>
          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-[#6B7280]">
              <strong>Propriétaire :</strong> {ownerName} | <strong>Exploitant :</strong> {companyName}<br />
              <strong>Siège social :</strong> {fullAddress}<br />
              <strong>TVA :</strong> {companyVat} | <strong>DPO :</strong> {dpoEmail}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/"><Button variant="ghost" className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button></Link>

        <h1 className="font-heading font-bold text-3xl text-[#111827] mb-8">Politique de Confidentialité</h1>
        <p className="text-sm text-[#6B7280] mb-6">Dernière mise à jour : Avril 2026 | Conforme au RGPD (Règlement UE 2016/679)</p>

        <div className="prose prose-lg max-w-none text-[#374151] space-y-6">
          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">1. Responsable du traitement</h2>
            <p>Le site buildconnect.be est la propriété de <strong>{ownerName}</strong> et est exploité par <strong>{companyName}</strong>.</p>
            <p>L'exploitant peut être modifié à tout moment par décision du propriétaire.</p>
            <p>Numéro d'entreprise : {companyVat}</p>
            <p>Siège social : {fullAddress}</p>
            <p>Email : {privacyEmail}</p>
            <p>Délégué à la protection des données (DPO) : {dpoEmail}</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">2. Données collectées</h2>
            <p>Nous collectons les catégories de données suivantes :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Données d'identification :</strong> nom de l'entreprise, numéro de TVA, adresse email, téléphone</li>
              <li><strong>Données professionnelles :</strong> corps de métier, certifications, expérience, portfolio</li>
              <li><strong>Données de localisation :</strong> adresse, code postal, coordonnées GPS (pour la carte interactive)</li>
              <li><strong>Données bancaires :</strong> IBAN (pour les coordonnées de facturation et les abonnements)</li>
              <li><strong>Données de connexion :</strong> adresse IP, cookies techniques, logs d'accès</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">3. Bases juridiques du traitement</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Exécution du contrat</strong> (Art. 6.1.b RGPD) : création de compte, mise en relation, gestion des paiements</li>
              <li><strong>Intérêt légitime</strong> (Art. 6.1.f RGPD) : amélioration des services, sécurité, prévention des fraudes</li>
              <li><strong>Obligation légale</strong> (Art. 6.1.c RGPD) : conservation des données comptables (10 ans - Code de droit économique belge)</li>
              <li><strong>Consentement</strong> (Art. 6.1.a RGPD) : envoi de newsletters et communications marketing</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">4. Finalités du traitement</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Gestion des comptes utilisateurs et authentification</li>
              <li>Mise en relation entre donneurs d'ordre et sous-traitants</li>
              <li>Gestion des paiements et abonnements (virements SEPA)</li>
              <li>Affichage des chantiers et sous-traitants sur la carte interactive</li>
              <li>Envoi de notifications relatives aux chantiers et candidatures</li>
              <li>Amélioration et sécurisation de la plateforme</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">5. Durée de conservation</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Données de compte :</strong> durée de vie du compte + 1 an après suppression</li>
              <li><strong>Données de transaction :</strong> 10 ans (obligation comptable belge)</li>
              <li><strong>Logs de connexion :</strong> 12 mois</li>
              <li><strong>Données marketing :</strong> jusqu'au retrait du consentement</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">6. Vos droits (RGPD)</h2>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Droit d'accès</strong> (Art. 15) : obtenir une copie de vos données</li>
              <li><strong>Droit de rectification</strong> (Art. 16) : corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement</strong> (Art. 17) : supprimer votre compte et vos données</li>
              <li><strong>Droit à la portabilité</strong> (Art. 20) : recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition</strong> (Art. 21) : vous opposer au traitement pour motif légitime</li>
              <li><strong>Droit de limitation</strong> (Art. 18) : restreindre le traitement de vos données</li>
            </ul>
            <p className="mt-3">Pour exercer vos droits : <strong>{privacyEmail}</strong></p>
            <p>Vous pouvez également introduire une réclamation auprès de l'<strong>Autorité de protection des données (APD)</strong> : <a href="https://www.autoriteprotectiondonnees.be" className="text-[#1E3A8A] underline" target="_blank" rel="noreferrer">www.autoriteprotectiondonnees.be</a></p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">7. Cookies</h2>
            <p>{platformName} utilise uniquement des <strong>cookies techniques strictement nécessaires</strong> au fonctionnement du service (authentification, session). Aucun cookie publicitaire ou de tracking n'est utilisé.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">8. Transferts internationaux</h2>
            <p>Vos données sont hébergées au sein de l'Union européenne. Aucun transfert hors UE n'est effectué sans garanties adéquates (décisions d'adéquation, clauses contractuelles types).</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-[#111827] mt-8 mb-3">9. Sécurité</h2>
            <p>Nous mettons en oeuvre des mesures techniques et organisationnelles pour protéger vos données : chiffrement HTTPS, hachage des mots de passe (bcrypt), accès restreint aux données, sauvegardes régulières.</p>
          </section>

          <section className="border-t pt-6 mt-8">
            <p className="text-sm text-[#6B7280]">
              <strong>Propriétaire :</strong> {ownerName} | <strong>Exploitant :</strong> {companyName}<br />
              <strong>Siège social :</strong> {fullAddress}<br />
              <strong>TVA :</strong> {companyVat} | <strong>DPO :</strong> {dpoEmail}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
